import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react"
import { AppstoreOutlined, TableOutlined } from "@ant-design/icons"
import { Card, Empty, Input, Table, Tooltip, Typography, message } from "antd"
import { ISapSupplierColumn, ISapSupplierPayloadItem } from "../../../types"
import { getAdaptiveColumnWidths, shouldShowCellTooltip } from "../../shared/utils/editTableSizing"

const { Text } = Typography

const PASTE_HIGHLIGHT_DURATION_MS = 2500

interface Props {
  columns: ISapSupplierColumn[]
  items: ISapSupplierPayloadItem[]
  disabled?: boolean
  onChange: (items: ISapSupplierPayloadItem[]) => void
}

interface IEditableSupplierColumn {
  key: string
  label: string
}

const normalizeHeaderValue = (value: unknown) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase()

const parseClipboardRows = (rawClipboard: string) =>
  rawClipboard
    .replace(/\r/g, "")
    .split("\n")
    .filter((line, index, source) => !(index === source.length - 1 && line === ""))
    .map((line) => line.split("\t"))
    .filter((row) => row.some((cell) => String(cell || "").trim() !== ""))

const isExportTitleRow = (row: string[] = []) => {
  const nonEmptyValues = row
    .map((cell) => String(cell || "").trim())
    .filter((cell) => cell.length > 0)

  if (nonEmptyValues.length !== 1) {
    return false
  }

  return normalizeHeaderValue(nonEmptyValues[0]).startsWith("SAP SUPPLIER")
}

const isMatchingHeaderRow = (
  row: string[] = [],
  columns: IEditableSupplierColumn[],
  startColumnIndex: number,
) => {
  if (!row.length) {
    return false
  }

  const expectedLabels = columns
    .slice(startColumnIndex, startColumnIndex + row.length)
    .map((column) => normalizeHeaderValue(column.label || column.key))

  if (expectedLabels.length !== row.length) {
    return false
  }

  return row.every((cell, index) => normalizeHeaderValue(cell) === expectedLabels[index])
}

const SupplierPayloadEditor: React.FC<Props> = ({ columns, items, disabled = false, onChange }) => {
  const [isMobile, setIsMobile] = useState(false)
  const [highlightedCells, setHighlightedCells] = useState<string[]>([])
  const highlightTimeoutRef = useRef<number | null>(null)

  const editableColumns = useMemo<IEditableSupplierColumn[]>(
    () =>
      columns.map((column, index) => ({
        key: column.key || `column_${index}`,
        label: column.label || column.key || `column_${index}`,
      })),
    [columns],
  )

  const buildHeaderCellStyle = (width?: number): CSSProperties => ({
    whiteSpace: "nowrap",
    wordBreak: "keep-all",
    minWidth: width,
    maxWidth: width,
  })

  const readCellValue = (item: ISapSupplierPayloadItem, key: string): string => {
    const rawValue = item[key as keyof ISapSupplierPayloadItem]
    if (rawValue === null || rawValue === undefined) return ""
    return String(rawValue)
  }

  const columnWidths = useMemo(() => getAdaptiveColumnWidths(editableColumns, items), [editableColumns, items])

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768)
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current)
      }
    },
    [],
  )

  const handleCellChange = (rowIndex: number, key: string, value: string) => {
    const nextItems = items.map((item, index) => {
      if (index !== rowIndex) return item
      return {
        ...item,
        [key]: value,
      }
    })
    onChange(nextItems)
  }

  const handleStructuredPaste = (event: React.ClipboardEvent<HTMLInputElement>, rowIndex: number, columnIndex: number) => {
    if (disabled || isMobile) return

    const rawClipboard = event.clipboardData.getData("text/plain")
    if (!rawClipboard.includes("\n") && !rawClipboard.includes("\t")) {
      return
    }

    const parsedRows = parseClipboardRows(rawClipboard)
    if (!parsedRows.length) {
      return
    }

    let workingRows = parsedRows

    if (isExportTitleRow(workingRows[0])) {
      workingRows = workingRows.slice(1)
    }

    if (workingRows.length && isMatchingHeaderRow(workingRows[0], editableColumns, columnIndex)) {
      workingRows = workingRows.slice(1)
    }

    if (!workingRows.length) {
      event.preventDefault()
      void message.warning("No Supplier data rows were found in the pasted Excel selection.")
      return
    }

    const matrixWidth = Math.max(...workingRows.map((row) => row.length))
    const remainingRows = items.length - rowIndex
    const remainingColumns = editableColumns.length - columnIndex

    if (workingRows.length > remainingRows) {
      event.preventDefault()
      void message.warning("The pasted Excel range does not fit the remaining Supplier rows.")
      return
    }

    if (matrixWidth > remainingColumns) {
      event.preventDefault()
      void message.warning("The pasted Excel columns do not match the remaining Supplier columns from the selected cell.")
      return
    }

    event.preventDefault()

    const normalizedRows = workingRows.map((row) =>
      Array.from({ length: matrixWidth }, (_, index) => String(row[index] || "")),
    )
    const targetKeys = editableColumns
      .slice(columnIndex, columnIndex + matrixWidth)
      .map((column) => column.key)

    const highlightKeys: string[] = []
    const nextItems = items.map((item, currentRowIndex) => {
      if (currentRowIndex < rowIndex || currentRowIndex >= rowIndex + normalizedRows.length) {
        return item
      }

      const rowValues = normalizedRows[currentRowIndex - rowIndex]
      const nextItem = { ...item }

      targetKeys.forEach((key, valueIndex) => {
        nextItem[key] = rowValues[valueIndex] || ""
        highlightKeys.push(`${currentRowIndex}:${key}`)
      })

      return nextItem
    })

    if (highlightTimeoutRef.current) {
      window.clearTimeout(highlightTimeoutRef.current)
    }

    setHighlightedCells(highlightKeys)
    highlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightedCells([])
      highlightTimeoutRef.current = null
    }, PASTE_HIGHLIGHT_DURATION_MS)

    onChange(nextItems)
    void message.success(`Pasted ${normalizedRows.length} Supplier row${normalizedRows.length > 1 ? "s" : ""} from Excel.`)
  }

  const getInputStyle = (rowIndex: number, key: string): CSSProperties | undefined => {
    if (!highlightedCells.includes(`${rowIndex}:${key}`)) {
      return undefined
    }

    return {
      backgroundColor: "#fff7cc",
      borderColor: "#f7c948",
    }
  }

  if (!items.length) {
    return (
      <Card
        title={
          <>
            <TableOutlined /> Edit Supplier Data
          </>
        }
        style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
      >
        <Empty description="No data to edit" />
      </Card>
    )
  }

  return (
    <Card
      title={
        <>
          {isMobile ? <AppstoreOutlined /> : <TableOutlined />} Edit Supplier Data
        </>
      }
      style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
      bodyStyle={{ padding: 0 }}
    >
      {isMobile ? (
        <div style={{ padding: 16, background: "#fafafa", maxHeight: 560, overflowY: "auto" }}>
          {items.map((item, rowIndex) => (
            <Card
              key={item._rowNumber || rowIndex}
              size="small"
              style={{ marginBottom: 16, borderRadius: 8, border: "1px solid #f0f0f0" }}
              title={`Row ${item._rowNumber || rowIndex + 1}`}
            >
              {editableColumns.map((column, columnIndex) => {
                const value = readCellValue(item, column.key)

                return (
                  <div
                    key={column.key}
                    style={{
                      marginBottom: columnIndex === editableColumns.length - 1 ? 0 : 12,
                      borderBottom: columnIndex === editableColumns.length - 1 ? "none" : "1px dashed #f0f0f0",
                      paddingBottom: columnIndex === editableColumns.length - 1 ? 0 : 12,
                    }}
                  >
                    <Text style={{ display: "block", fontSize: 12, color: "#8c8c8c", marginBottom: 6 }}>
                      {column.label}
                    </Text>
                    <Input value={value} disabled={disabled} onChange={(event) => handleCellChange(rowIndex, column.key, event.target.value)} />
                  </div>
                )
              })}
            </Card>
          ))}
        </div>
      ) : (
        <div>
          <div
            style={{
              margin: "16px 16px 0",
              padding: "12px 16px",
              background: "#faf7f0",
              border: "1px solid #f0d8a8",
              borderRadius: 8,
              color: "#8c6b1f",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Excel paste is supported in desktop edit mode</div>
            <div style={{ fontSize: 13 }}>
              You can paste rows directly from the exported Supplier Excel file. Title and header rows from Export Data Updated are ignored automatically.
            </div>
          </div>
          <Table
            className="supplier-payload-editor-table"
            rowKey={(row, index) => row._rowNumber?.toString() || index?.toString() || "0"}
            dataSource={items}
            pagination={false}
            size="middle"
            scroll={{ x: "max-content", y: 620 }}
            tableLayout="auto"
            columns={[
              {
                title: "Row",
                key: "_rowNumber",
                width: 70,
                onHeaderCell: () => ({
                  style: buildHeaderCellStyle(70),
                }),
                render: (_value: unknown, row: ISapSupplierPayloadItem, rowIndex?: number) => row._rowNumber || (rowIndex || 0) + 1,
              },
              ...editableColumns.map((column, columnIndex) => {
                const baseLabel = column.label
                const columnWidth = columnWidths[column.key] || 160

                return {
                  title: (
                    <Tooltip title={baseLabel}>
                      <div
                        style={{
                          display: "block",
                          color: "#ffffff",
                          fontWeight: 600,
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          minWidth: columnWidth - 24,
                        }}
                      >
                        {baseLabel}
                      </div>
                    </Tooltip>
                  ),
                  dataIndex: column.key,
                  key: column.key,
                  width: columnWidth,
                  onHeaderCell: () => ({
                    style: buildHeaderCellStyle(columnWidth),
                  }),
                  render: (_value: unknown, _: ISapSupplierPayloadItem, rowIndex?: number) => {
                    const currentValue = readCellValue(items[rowIndex || 0] || {}, column.key)
                    const inputNode = (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <Text
                          style={{
                            display: "block",
                            fontSize: 11,
                            color: "#8c8c8c",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {baseLabel}
                        </Text>
                        <Input
                          value={currentValue}
                          disabled={disabled}
                          onChange={(event) => handleCellChange(rowIndex || 0, column.key, event.target.value)}
                          onPaste={(event) => handleStructuredPaste(event, rowIndex || 0, columnIndex)}
                          style={getInputStyle(rowIndex || 0, column.key)}
                        />
                      </div>
                    )

                    if (!shouldShowCellTooltip(currentValue, columnWidth)) {
                      return inputNode
                    }

                    return <Tooltip title={currentValue}>{inputNode}</Tooltip>
                  },
                }
              }),
            ]}
            style={{ borderTop: "1px solid #f0f0f0", marginTop: 16 }}
          />
        </div>
      )}
    </Card>
  )
}

export default SupplierPayloadEditor
